import { Autocomplete } from "@base-ui/react/autocomplete";
import { usePortalContainer } from "@/context/PortalContext";
import { cn } from "@/lib/utils";

export interface SuggestionItem {
  value: string;
  label: string;
}

export interface SuggestionInputProps {
  value: string;
  onValueChange: (value: string) => void;
  items: SuggestionItem[];
  /** Fires only when the user explicitly picks an item from the list. */
  onItemSelect?: (item: SuggestionItem) => void;
  onBlur?: () => void;
  placeholder?: string;
  invalid?: boolean;
  autoComplete?: string;
  type?: string;
  id?: string;
  ariaLabel?: string;
  inputClassName?: string;
}

export function SuggestionInput({
  value,
  onValueChange,
  items,
  onItemSelect,
  onBlur,
  placeholder,
  invalid = false,
  autoComplete,
  type = "text",
  id,
  ariaLabel,
  inputClassName,
}: SuggestionInputProps) {
  const portalContainer = usePortalContainer();

  const borderClass = invalid
    ? "tw:border-red-400 focus:tw:border-red-500"
    : "tw:border-gray-300 focus:tw:border-[#523D33]";

  return (
    <Autocomplete.Root
      items={items}
      value={value}
      mode="list"
      autoHighlight={false}
      openOnInputClick={false}
      onValueChange={(next, details) => {
        onValueChange(next);
        if (details.reason === "item-press") {
          const picked = items.find((i) => i.label === next);
          if (picked) onItemSelect?.(picked);
        }
      }}
    >
      <Autocomplete.Input
        id={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoCorrect="off"
        spellcheck={false}
        aria-label={ariaLabel}
        onBlur={onBlur}
        className={cn(
          "tw:w-full tw:rounded-md tw:border tw:bg-white tw:px-2 tw:py-1.5 tw:text-sm tw:outline-none",
          borderClass,
          inputClassName,
        )}
      />
      <Autocomplete.Portal container={portalContainer ?? undefined}>
        <Autocomplete.Positioner sideOffset={4} className="tw:z-80 tw:outline-none">
          <Autocomplete.Popup
            className={cn(
              "tw:bg-white tw:rounded-lg tw:border tw:border-[#FDEDEE] tw:shadow-lg",
              "tw:overflow-hidden tw:w-(--anchor-width)",
              "tw:data-open:animate-in tw:data-closed:animate-out tw:data-open:fade-in-0 tw:data-closed:fade-out-0 tw:data-open:zoom-in-95 tw:data-closed:zoom-out-95 tw:duration-150 tw:ease-out",
            )}
          >
            <Autocomplete.Empty className="tw:px-3 tw:py-2 tw:text-xs tw:text-gray-400">
              No matches
            </Autocomplete.Empty>
            <Autocomplete.List className="tw:max-h-56 tw:overflow-y-auto tw:py-1 tw:outline-none">
              {(item: SuggestionItem) => (
                <Autocomplete.Item
                  key={item.value}
                  value={item}
                  className={cn(
                    "tw:flex tw:items-center tw:px-3 tw:py-1.5 tw:text-sm tw:cursor-pointer tw:select-none tw:outline-none",
                    "tw:text-[#523D33]",
                    "tw:data-highlighted:bg-[#FDEDEE] tw:data-selected:bg-[#FDEDEE] tw:data-selected:font-medium",
                  )}
                >
                  {item.label}
                </Autocomplete.Item>
              )}
            </Autocomplete.List>
          </Autocomplete.Popup>
        </Autocomplete.Positioner>
      </Autocomplete.Portal>
    </Autocomplete.Root>
  );
}
